import Input from '@components/Input/Input'
import GenericModal from '@components/Modal/Modal'
import { getHomeSearchResults } from 'helpers/homeSearch'
import { getNativeReaderHref } from 'helpers/nativeReaderHref'
import Link from 'next/link'
import { ChangeEvent, RefObject, useRef, useState, type FC } from 'react'
import { FaArrowRight } from 'react-icons/fa'

export type FilterResult = {
  name: string
  keywords: string
  url: string
  highlight?: boolean
}

type HomePageSearchModalProps = {
  isOpen: boolean
  triggerCloseModal: VoidFunction
}

/** Renders global search immediately from local metadata, including while offline. */
const HomePageSearchModal: FC<HomePageSearchModalProps> = ({
  isOpen,
  triggerCloseModal: onCloseModal,
}) => {
  const searchInput = useRef<HTMLInputElement>(null)
  /** Updates the local query as the user types. */
  const searchHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setWord(event.target.value)
  }
  const [word, setWord] = useState('')

  const items = getHomeSearchResults(word)

  return (
    <GenericModal
      triggerCloseModal={() => {
        onCloseModal()
        setWord('')
      }}
      id="home-search"
      isOpen={isOpen}
      showCloseIcon={false}
      initialFocusRef={searchInput as RefObject<HTMLElement>}
    >
      <div className="flex flex-col min-h-dvh md:min-h-0 md:h-auto px-4 py-4 p-0 md:px-0 md:pb-4 md:pt-2 bg-main md:bg-transparent">
        <div className="sticky top-0 z-10 pb-2 pt-2 md:pt-0 flex items-center gap-2 md:px-6">
          <label htmlFor="searchInput" className="sr-only">
            Arama yapın
          </label>
          <Input
            className="mb-0 px-5 py-4 text-lg md:text-base sacred-shadow flex-1 bg-surface-subtle border-transparent focus:border-brand-secondary focus:bg-main transition-all"
            type="search"
            name="searchInput"
            value={word}
            onChange={searchHandler}
            id="searchInput"
            placeholder="Ayet, Sure, Sayfa, Cüz, Dua..."
            autoComplete="off"
            ref={searchInput}
          />
          <button
            onClick={() => {
              onCloseModal()
              setWord('')
            }}
            className="md:hidden px-2 py-4 text-main font-medium active:opacity-70 transition-opacity whitespace-nowrap"
          >
            Vazgeç
          </button>
        </div>
        <div className="mt-2 pb-8 md:pb-2 md:px-4">
          <ul className="space-y-1">
            {items.length ? (
              items.map((item, key) => {
                return (
                  <li
                    key={key}
                    className="nav-item-animate"
                    style={{ animationDelay: `${(key % 10) * 50}ms` }}
                  >
                    <Link
                      href={getNativeReaderHref(item.url)}
                      className="flex items-center justify-between p-3 md:p-4 rounded-xl transition-all duration-200 hover:bg-brand-secondary-subtle active:scale-[0.98] group"
                    >
                      <span className="text-lg font-medium text-main group-hover:text-brand-secondary transition-colors">
                        {item.name}
                      </span>
                      <FaArrowRight className="text-muted opacity-50 group-hover:opacity-100 group-hover:text-brand-secondary transition-all transform group-hover:translate-x-1" />
                    </Link>
                  </li>
                )
              })
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-muted">
                <span className="text-4xl mb-4 opacity-50">🔍</span>
                <p className="text-lg">Sonuç bulunamadı...</p>
                <p className="text-sm opacity-70 mt-1 text-center max-w-[250px]">
                  Farklı kelimelerle veya sure adıyla aramayı deneyin.
                </p>
              </div>
            )}
          </ul>
        </div>
      </div>
    </GenericModal>
  )
}

export default HomePageSearchModal
